<script setup lang="ts">
import { toDisplayValue } from '@/utils/numberUtils.ts';
import { toRef, computed, watch, ref } from 'vue';
import {
  CreateBeamQualityDto,
  CreateElectronBeamQualityDto,
  PhotonBeamQualityDto,
  ElectronBeamQualityDto,
  CreatePhotonBeamQualityDto,
  BeamQualityDto,
  CreateElectronBeamQualitiesDtoTypeEnum,
  CreatePhotonBeamQualitiesDtoTypeEnum,
  NumericValueDto,
} from '@/api/generator';
import Dialog from '@/components/Dialog/Dialog.vue';
import VeeSubmitButton from '@/components/forms/SubmitButton/VeeSubmitButton.vue';
import VeeTextField from '@/components/forms/TextField/VeeTextField.vue';
import TextField from '@/components/forms/TextField/TextField.vue';
import VeeNumberField from '@/components/forms/NumberField/VeeNumberField.vue';
import VeeSelect from '@/components/forms/Select/VeeSelect.vue';
import FormLabel from '@/components/FormLabel/FormLabel.vue';

import { useVeeForm } from '@/components/forms/useVeeForm';
import { cloneDeep, difference } from 'lodash';
import { TargetBeamQuality } from '../types';
import { I18nKey } from '@/utils/typings';
import { api } from '@/api';
import { useField, useFieldArray } from 'vee-validate';
import { useTranslation } from 'i18next-vue';
import { toCreateBeamQualityDto } from '../mappings';
import { translateEntityProperty, truthy } from '@/utils/helper';
import { backendValidator } from '@/validation/validators/backendValidator';

interface BeamQualityTableAddDialogProps {
  target?: TargetBeamQuality;
  ownerIsInUse?: boolean;
}

const props = defineProps<BeamQualityTableAddDialogProps>();

const { fields: photonItems } = useFieldArray<PhotonBeamQualityDto>(
  toRef(() => 'beamQualitiesPhoton.values'),
);

const { fields: electronItems } = useFieldArray<ElectronBeamQualityDto>(
  toRef(() => 'beamQualitiesElectron.values'),
);

const emits = defineEmits<{
  (event: 'confirm', beamQuality: BeamQualityDto, index?: number): void;
  (event: 'cancel'): void;
}>();

const { t } = useTranslation();

const {
  form: { handleSubmit, values, resetForm, errors, validate },
} = useVeeForm<BeamQualityDto>({
  mode: 'add',
  schemaName: 'BeamQualityDto',
  confirmationOptions: {
    skipDirtyCheck: 'always', // This is an inner form that does not need a dirty check
  },
  customValidateFunctions: [
    backendValidator((beamQuality, errorHandlers) => {
      if (!beamQuality.$type || !props.target) {
        return Promise.resolve({} as any);
      }

      const beamQualityToValidate = toCreateBeamQualityDto(beamQuality);

      const beamQualitiesPhoton = photonItems.value.map((item) =>
        toCreateBeamQualityDto(item.value),
      );

      const beamQualitiesElectron = electronItems.value.map((item) =>
        toCreateBeamQualityDto(item.value),
      );

      const removeActiveItemFromArray = (
        beamQualities: CreateBeamQualityDto[],
      ): CreateBeamQualityDto[] =>
        beamQualities.filter((item) => item.name !== props.target?.beamQuality.name);

      return api.medicalSystemsApi.validateBeamQuality(
        {
          validateBeamQualityDto: {
            beamQualityToValidate,
            beamQualitiesPhoton: {
              values: removeActiveItemFromArray(
                beamQualitiesPhoton,
              ) as CreatePhotonBeamQualityDto[],
              $type:
                CreatePhotonBeamQualitiesDtoTypeEnum.PtwPtw100AppZeroApplicationSharedMedicalSystemComponentDtoTreatmentMachinesConfigurationCreatePhotonBeamQualitiesDtoPtwPtw100AppZeroApplicationShared,
            },
            beamQualitiesElectron: {
              values: removeActiveItemFromArray(
                beamQualitiesElectron,
              ) as CreateElectronBeamQualityDto[],
              $type:
                CreateElectronBeamQualitiesDtoTypeEnum.PtwPtw100AppZeroApplicationSharedMedicalSystemComponentDtoTreatmentMachinesConfigurationCreateElectronBeamQualitiesDtoPtwPtw100AppZeroApplicationShared,
            },
          },
        },
        errorHandlers,
      );
    }),
  ],
});

const isInUse = computed(() => props.ownerIsInUse);

const availableDoseRates = computed(() =>
  values.availableDoseRates?.values ? values.availableDoseRates.values.join('; ') : '',
);
const initialDoseRatesOnOpen = computed(() =>
  props.target?.initialBeamQuality?.availableDoseRates.values?.join('; '),
);

const additionalDoseRates = ref('');

const availableDoseRatesField = useField('availableDoseRates');

function onDoseRatesFieldUpdate(input: string | undefined) {
  let stringArray: Array<string> = input?.split(';') || [];

  if (isInUse.value && inEditMode.value) {
    const initialArray = initialDoseRatesOnOpen.value?.split(';') || [];
    stringArray = [...initialArray, ...stringArray];
  }

  const numArray: Array<number> = stringArray
    .map((el) => Number.parseInt(el, 10))
    .filter((parsed) => !Number.isNaN(parsed));

  availableDoseRatesField.setValue(
    {
      ...values.availableDoseRates,
      values: numArray,
    },
    availableDoseRatesField.meta.touched,
  );
}

const highDoseTechniqueTypeOptions = computed(() =>
  props.target?.beamQuality.highDoseTechniqueType?.options?.map((option) => {
    return {
      title: translateEntityProperty('name', option),
      value: option,
    };
  }),
);

const primaryFluenceShapings = computed(() => {
  if (props.target?.beamQuality && 'primaryFluenceShaping' in props.target.beamQuality) {
    return (
      props.target?.beamQuality?.primaryFluenceShaping?.options?.map((option) => ({
        title: translateEntityProperty('name', option),
        value: option,
      })) || []
    );
  } else {
    return [];
  }
});

function getEnergyName(energy?: NumericValueDto): string {
  if (!energy?.value) return '';

  const { value, decimalPlaces, unitAbbreviation, valueType } = energy;
  const fractionDigits = value % 1 === 0 ? 0 : decimalPlaces;
  return toDisplayValue(value, { fractionDigits, unitAbbreviation, valueType });
}

function constructName(beamQuality: BeamQualityDto) {
  const constructedName =
    [
      getEnergyName(beamQuality.energy),

      'primaryFluenceShaping' in beamQuality
        ? translateEntityProperty('name', beamQuality.primaryFluenceShaping?.value)
        : null,

      translateEntityProperty('name', beamQuality.highDoseTechniqueType?.value),
    ]
      .filter(truthy)
      .join(' ') || undefined;

  return constructedName;
}

const nameField = useField<string | undefined>('name');

// reconstruct name when params changed and old name was either constructed or is undefined
watch(
  () => cloneDeep(values), // this is expensive but needed, otherwise we miss parameter updates
  (newBeamQuality, oldBeamquality) => {
    const nameChanged = oldBeamquality.name !== newBeamQuality.name;
    const oldNameWasConstructed = oldBeamquality.name === constructName(oldBeamquality);
    const noNameSet = oldBeamquality.name === undefined;
    const isInitialChange = noNameSet;

    if (!nameChanged && (noNameSet || oldNameWasConstructed)) {
      nameField.setValue(constructName(newBeamQuality), !isInitialChange);
    }
  },
);

watch(
  () => props.target?.beamQuality,
  (newBeamQuality) => {
    additionalDoseRates.value = difference(
      props.target?.beamQuality.availableDoseRates.values || [],
      props.target?.initialBeamQuality?.availableDoseRates.values || [],
    ).join(';');

    resetForm(
      { values: { ...newBeamQuality, name: newBeamQuality?.name || undefined } },
      {
        force: true,
      },
    );
  },
);

const inEditMode = computed(() => {
  return !!props.target?.index || props.target?.index === 0;
});

const submit = handleSubmit(() => {
  const clonedValues = cloneDeep(values);
  clonedValues.availableDoseRates.values?.sort((a, b) => a - b);
  emits('confirm', clonedValues, props.target?.index);
  emits('cancel');
});
</script>

<template>
  <Dialog
    :modelValue="Boolean(props.target)"
    size="medium"
    @update:modelValue="
      (open) => {
        if (!open) {
          emits('cancel');
        }
      }
    "
  >
    <template #title>
      {{
        props.target?.index || props.target?.index === 0
          ? $t('EditBeamQuality')
          : $t('AddBeamQuality')
      }}
    </template>

    <template #text>
      <v-row>
        <v-col
          ><FormLabel :label="$t(values.radiationType?.name as I18nKey)">{{
            $t(values.radiationType?.value?.name as I18nKey)
          }}</FormLabel></v-col
        >

        <v-col>
          <VeeNumberField
            name="energy.value"
            :label="$t(values.energy?.name as I18nKey)"
            :suffix="values.energy?.unitAbbreviation"
            :placeholder="$t('Required')"
            :disabled="inEditMode && ownerIsInUse"
        /></v-col>
      </v-row>
      <v-row class="gutterBottom align-end">
        <v-col
          ><VeeSelect
            v-if="'primaryFluenceShaping' in values && values.primaryFluenceShaping != null"
            :clearable="true"
            name="primaryFluenceShaping.value"
            :items="primaryFluenceShapings"
            :label="$t(values.primaryFluenceShaping.name as I18nKey)"
            :disabled="inEditMode && ownerIsInUse"
          />
        </v-col>

        <v-col>
          <VeeSelect
            :clearable="true"
            name="highDoseTechniqueType.value"
            :items="highDoseTechniqueTypeOptions"
            :label="$t(values.highDoseTechniqueType?.name as I18nKey)"
            :disabled="inEditMode && ownerIsInUse"
          >
          </VeeSelect>
        </v-col>
      </v-row>

      <VeeTextField
        class="gutterTop"
        name="name"
        :label="$t('Name')"
        :placeholder="$t('Required')"
      />

      <v-divider thickness="2" class="gutterTop" />
      <div v-if="isInUse && inEditMode">
        <TextField
          class="mt-6"
          :label="t('AvailableDoseRatesForStaticBeams')"
          :modelValue="initialDoseRatesOnOpen"
          mode="details"
          :suffix="values.availableDoseRates?.unitAbbreviation || ''"
          :placeholder="$t('SingleValuesSeparatedBySemicolonPlaceholder')"
        />
        <TextField
          v-model="additionalDoseRates"
          :errorMessage="errors.availableDoseRates"
          class="mt-6"
          :label="t('AdditionalDoseRates')"
          mode="edit"
          :suffix="values.availableDoseRates?.unitAbbreviation || ''"
          :placeholder="$t('SingleValuesSeparatedBySemicolonPlaceholder')"
          @blur="onDoseRatesFieldUpdate($event.target.value)"
        />
      </div>
      <TextField
        v-else
        :errorMessage="errors.availableDoseRates"
        class="mt-6"
        :label="t('AvailableDoseRatesForStaticBeams')"
        :modelValue="availableDoseRates"
        mode="edit"
        :suffix="values.availableDoseRates?.unitAbbreviation || ''"
        :placeholder="$t('SingleValuesSeparatedBySemicolonPlaceholder')"
        @update:modelValue="onDoseRatesFieldUpdate"
        @blur="validate"
      />
    </template>

    <template #actions>
      <VeeSubmitButton
        :text="props.target?.index || props.target?.index === 0 ? $t('Save') : $t('AddAndSelect')"
        @click="submit"
      ></VeeSubmitButton>
    </template>
  </Dialog>
</template>
